import { create } from 'zustand'
import type { Asset, Indication, KeyInsightQuestion } from '@/types'
import type { TaxonomyPayload } from '@/types/taxonomy'
import { fetchAndCacheTaxonomy, getCachedTaxonomy } from '@/lib/taxonomyClient'

type TaxonomyStatus = 'idle' | 'loading' | 'ready' | 'error'

interface TaxonomyState {
  data: TaxonomyPayload | null
  status: TaxonomyStatus
  error: string | null
  lastLoadedFromCache: boolean
  refreshInFlight: Promise<void> | null
  init: () => Promise<void>
  refresh: () => Promise<void>
}

export const useTaxonomyStore = create<TaxonomyState>((set, get) => ({
  data: null,
  status: 'idle',
  error: null,
  lastLoadedFromCache: false,
  refreshInFlight: null,
  init: async () => {
    if (get().status !== 'idle') {
      return
    }

    set({ status: 'loading', error: null })

    const cache = await getCachedTaxonomy()
    if (cache) {
      set({
        data: cache.payload,
        status: 'ready',
        error: null,
        lastLoadedFromCache: true,
      })
    }

    try {
      await get().refresh()
    } catch (error) {
      if (!cache) {
        set({
          status: 'error',
          error: error instanceof Error ? error.message : 'Failed to load taxonomy',
        })
      }
    }
  },
  refresh: async () => {
    if (get().refreshInFlight) {
      return get().refreshInFlight!
    }

    const promise = (async () => {
      try {
        const result = await fetchAndCacheTaxonomy()
        set({
          data: result.payload,
          status: 'ready',
          error: null,
          lastLoadedFromCache: result.fromCache,
        })
      } catch (error) {
        set({
          status: 'error',
          error: error instanceof Error ? error.message : 'Failed to refresh taxonomy',
        })
        throw error
      } finally {
        set({ refreshInFlight: null })
      }
    })()

    set({ refreshInFlight: promise })
    return promise
  },
}))

export function getTaxonomySnapshot(): TaxonomyPayload | null {
  return useTaxonomyStore.getState().data
}

export function resetTaxonomyStore(): void {
  useTaxonomyStore.setState((state) => ({
    ...state,
    data: null,
    status: 'idle',
    error: null,
    lastLoadedFromCache: false,
    refreshInFlight: null,
  }))
}

export function getAssets(): Asset[] {
  const taxonomy = getTaxonomySnapshot()
  if (!taxonomy) {
    return []
  }
  return [...taxonomy.assets].sort((a, b) => a.order - b.order)
}

export function getIndicationsByAsset(assetId: string): Indication[] {
  const taxonomy = getTaxonomySnapshot()
  if (!taxonomy) {
    return []
  }
  return taxonomy.indications
    .filter((ind) => ind.assetId === assetId)
    .sort((a, b) => a.order - b.order)
}

export function getKiqsByIndication(indicationId: string): KeyInsightQuestion[] {
  const taxonomy = getTaxonomySnapshot()
  if (!taxonomy) {
    return []
  }
  return taxonomy.keyInsightQuestions
    .filter((kiq) => kiq.indicationId === indicationId)
    .sort((a, b) => a.order - b.order)
}

export function getIndicationById(id: string): Indication | undefined {
  const taxonomy = getTaxonomySnapshot()
  return taxonomy?.indications.find((ind) => ind.id === id)
}

export function getAssetById(id: string): Asset | undefined {
  const taxonomy = getTaxonomySnapshot()
  return taxonomy?.assets.find((asset) => asset.id === id)
}

export function getKiqById(id: string): KeyInsightQuestion | undefined {
  const taxonomy = getTaxonomySnapshot()
  return taxonomy?.keyInsightQuestions.find((kiq) => kiq.id === id)
}
